import { first, isEqual } from 'lodash';
import { BaseForm } from '@/modules/core/app/models/abstracts/BaseForm';
import ApiColumn from '@/modules/core/app/models/api/ApiColumn';
import { ClientSelectOption } from '@/modules/ta/client/models/ClientSelectOption';
import { ClusterSelectOption } from '@/modules/ta/cluster/models/ClusterSelectOption';
import { ClientGroupSelectOption } from '@/modules/ta/clientgroup/models/ClientGroupSelectOption';
import { DashboardFilterItem } from '@/modules/core/dashboard/models/DashboardFilterItem';
import { DataSourceType } from '@/modules/core/app/constants/datasource.constants';
import { SmartCampaignSelectOption } from '@/modules/ta/smartcampaign/models/SmartCampaignSelectOption';
import { getters } from '@/modules/core/app/helpers/store';
import { AppModule } from '@/modules/core/app/constants/app.constants';
import { deepClone } from '@/modules/core/helper';
import { WidgetFilterConditionType } from '@/modules/ta/widget/widget.constants';

export class DashboardFilterForm extends BaseForm {
  initialData;

  visibleFields;

  constructor({
    data,
    visibleFields = [
      DataSourceType.CLIENT,
      DataSourceType.CLIENT_GROUP,
      DataSourceType.CLUSTER,
      DataSourceType.SMART_CAMPAIGN,
    ],
    clientGroupIds = [],
  }) {
    const userSettings = getters.session.getUserSettings();
    const clientLabel = userSettings.isModuleAvailable(AppModule.BRAND_PACKAGE)
      ? i18n.$t('Campaign')
      : i18n.$t('Client');
    const clientGroupLabel = userSettings.isModuleAvailable(AppModule.BRAND_PACKAGE)
      ? i18n.$t('Campaign Group')
      : i18n.$t('Client Group');

    const columns = [];
    if (visibleFields.includes(DataSourceType.CLIENT)) {
      columns.push(
        new ApiColumn({
          // eslint-disable-next-line tap/no-raw-text-js
          field: 'client',
          label: clientLabel,
        })
      );
    }

    if (visibleFields.includes(DataSourceType.CLIENT_GROUP)) {
      columns.push(
        new ApiColumn({
          // eslint-disable-next-line tap/no-raw-text-js
          field: 'clientGroup',
          label: clientGroupLabel,
        })
      );
    }

    if (visibleFields.includes(DataSourceType.CLUSTER)) {
      columns.push(
        new ApiColumn({
          // eslint-disable-next-line tap/no-raw-text-js
          field: 'cluster',
          label: i18n.$t('Business Unit'),
        })
      );
    }

    if (visibleFields.includes(DataSourceType.SMART_CAMPAIGN)) {
      columns.push(
        new ApiColumn({
          // eslint-disable-next-line tap/no-raw-text-js
          field: 'smartCampaign',
          label: i18n.$t('SmartCampaign'),
        })
      );
    }

    /**
     * @param key
     * @returns {*}
     */
    function mapFilterValue(key) {
      const filter = first(data[key].values);
      return filter
        ? {
            key: filter.id,
            value: filter.text,
          }
        : null;
    }

    const newData = {
      client: data[DataSourceType.CLIENT] ? mapFilterValue(DataSourceType.CLIENT) : null,
      clientGroup: data[DataSourceType.CLIENT_GROUP]
        ? mapFilterValue(DataSourceType.CLIENT_GROUP)
        : null,
      cluster: data[DataSourceType.CLUSTER] ? mapFilterValue(DataSourceType.CLUSTER) : null,
      smartCampaign: data[DataSourceType.SMART_CAMPAIGN]
        ? mapFilterValue(DataSourceType.SMART_CAMPAIGN)
        : null,
    };

    super({ data: newData, columns });

    this.initialData = deepClone(newData);

    this.clientOptions = new ClientSelectOption({ clientGroupIds });
    this.clientGroupOptions = new ClientGroupSelectOption();
    this.clusterOptions = new ClusterSelectOption();
    this.smartCampaignOption = new SmartCampaignSelectOption();
    this.visibleFields = visibleFields;
  }

  hasSelectedEntity() {
    return this.getSelectedEntity() !== null;
  }

  getSelectedEntity() {
    return (
      this.data.client || this.data.clientGroup || this.data.cluster || this.data.smartCampaign
    );
  }

  getSelectedEntityType() {
    if (this.data.client) {
      return DataSourceType.CLIENT;
    }
    if (this.data.clientGroup) {
      return DataSourceType.CLIENT_GROUP;
    }
    if (this.data.cluster) {
      return DataSourceType.CLUSTER;
    }
    if (this.data.smartCampaign) {
      return DataSourceType.SMART_CAMPAIGN;
    }
    return null;
  }

  hasChanged() {
    return !isEqual(this.initialData, this.data);
  }

  /**
   * @returns {object}
   */
  getModel() {
    if (!this.hasSelectedEntity()) {
      return {};
    }

    const selectedEntity = this.getSelectedEntity();
    const selectedEntityType = this.getSelectedEntityType();
    const model = {};
    model[selectedEntityType] = new DashboardFilterItem({
      values: [
        {
          id: selectedEntity[this.clientOptions.primaryKey],
          text: selectedEntity[this.clientOptions.valueKey],
        },
      ],
    });
    return model;
  }

  getResetModel() {
    return {
      widget_data_warehouse: [],
      widget_sourced: {
        [getters.dashboard.getDashboard()?.id]: [],
        values: [],
      },
      /* eslint-disable tap/no-raw-text-js */
      [DataSourceType.CLIENT]: { field: 'id', values: [], type: WidgetFilterConditionType.IN },
      [DataSourceType.CLIENT_GROUP]: {
        field: 'id',
        values: [],
        type: WidgetFilterConditionType.IN,
      },
      [DataSourceType.CLUSTER]: { field: 'id', values: [], type: WidgetFilterConditionType.IN },
      [DataSourceType.SMART_CAMPAIGN]: { field: 'id', values: [] },
      /* eslint-enable tap/no-raw-text-js */
    };
  }

  resetSelectedFilters() {
    this.data.client = null;
    this.data.clientGroup = null;
    this.data.cluster = null;
    this.data.smartCampaign = null;
  }
}
